import React from 'react';
import PropTypes from 'prop-types';
import { observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import { FormattedMessage, defineMessages } from 'react-intl';
import Styled from 'styled';
import { URLBuilder } from 'url-builder/url-builder';
import Helmet from 'helmet/helmet';

import { URLBuilderLink } from 'lib/url/url-builder-link';
import RouteWithSearch from 'lib/react-router/route-with-search';
import Switch from 'lib/react-router/loading-switch';

import { HideForVendor } from 'four-nets/vendor/permissions';
import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import { CategoryStore } from 'four-nets/forum/forum';
import BigButtonView from 'four-nets/ui/big-button/big-button-view';
import TitleView from 'four-nets/ui/title-view';
import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import { BreadcrumbsViewModel } from 'four-nets/ui/breadcrumbs-view';
import SearchPanel from 'four-nets/search/search-panel/search-panel';
import NewThreadView, { NewThreadViewModel } from 'four-nets/forum/thread/new-thread/new-thread-view';
import { Site } from 'four-nets/site';

import CategoryNotificationButton from './category-notification-button/category-notification-button';
import CategoryThreadsLoader from './category-threads-loader';
import { CategoryLink } from '../category-link';
import defaultStyles from './category-view.scss';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';

@Styled(defaultStyles)
@observer
class CategoryView extends React.Component {
    static contextTypes = {
        api: PropTypes.object,
        app: PropTypes.object,
        categoryStore: PropTypes.instanceOf(CategoryStore),
        site: PropTypes.instanceOf(Site),
        urlBuilder: PropTypes.instanceOf(URLBuilder),
        authCheck: PropTypes.func,
    };

    newThreadView;

    @observable breadcrumbsViewModel;
    @observable newThreadViewModel;

    update = () => {
        const { category } = this.props;

        this.breadcrumbsViewModel = new BreadcrumbsViewModel({
            links: [
                new URLBuilderLink({
                    url: 'forum',
                    label: defineMessages({ FORUM_LINK_LABEL: { id: 'FORUM_LINK_LABEL' } }).FORUM_LINK_LABEL,
                }),
                new CategoryLink({ category }),
            ],
        });

        this.newThreadViewModel = new NewThreadViewModel({
            categoryStore: this.context.categoryStore,
            hidePrivateDiscussions: category.superCategoryRow > 0,
            userStore: this.context.categoryStore.userStore,
        });
    };

    componentDidMount() {
        this.disposer = reaction(() => this.props.category, this.update);
    }

    componentWillUnmount() {
        if (this.disposer) {
            this.disposer();
        }
    }

    setNewThreadView = ref => {
        this.newThreadView = ref;
    };

    onNewThreadClick = () => {
        const { newThreadView } = this;

        if (newThreadView) {
            const action = () => {
                newThreadView.show({
                    form: this.newThreadViewModel.setupForm({ category: this.props.category }),
                });
            };

            this.context.authCheck(action, AuthOrigin.NewThread);
        }
    };

    renderHelmet = H => {
        const { category } = this.props;
        const { site } = this.context;

        if (site.isLiving) {
            return (
                <FormattedMessage id='PAGE_TITLE_INDEX_ALL_ON_LIVING'>
                    {message => {
                        const title = `${category?.name} | ${site.name}`;

                        return (
                            <H>
                                <title>{title}</title>
                                <meta property='og:title' content={title} />
                                <meta name='description' content={message} />
                                <meta property='og:description' content={message} />
                            </H>
                        );
                    }}
                </FormattedMessage>
            );
        } else if (site.isWedding) {
            return (
                <FormattedMessage id='PAGE_TITLE_INDEX_ALL_ON_WEDDING'>
                    {message => {
                        const title = `${category?.name} | ${site.name}`;
                        return (
                            <H>
                                <title>{title}</title>
                                <meta property='og:title' content={title} />
                                <meta name='description' content={message} />
                                <meta property='og:description' content={message} />
                            </H>
                        );
                    }}
                </FormattedMessage>
            );
        } else {
            return (
                <FormattedMessage id='PAGE_TITLE_INDEX_ALL_ON_GARDEN'>
                    {message => {
                        const title = `${category?.name} | ${site.name}`;
                        return (
                            <H>
                                <title>{title}</title>
                                <meta property='og:title' content={title} />
                                <meta name='description' content={message} />
                                <meta property='og:description' content={message} />
                            </H>
                        );
                    }}
                </FormattedMessage>
            );
        }
    };

    render() {
        const { styles, category } = this.props;
        const { newThreadViewModel } = this;
        const { site } = this.context;

        const isTargetedPromoCampaign = category?.slug === 'dni-s-heluz';
        const isNOTTargetedPromoCampaign = !isTargetedPromoCampaign;

        return (
            <div className={styles.outterWrapper}>
                <div className={styles.category}>
                    <Helmet render={this.renderHelmet} />
                    <div ref={this.setRef} className={styles.wrapper}>
                        {isNOTTargetedPromoCampaign ? (
                            <TitleView
                                style={{
                                    title: styles.titleExtend,
                                    components: styles.componentsExtend,
                                }}
                                title={<h1 className={styles.heading}>{category?.name}</h1>}
                                leftComponent={
                                    category && category.superCategoryRow && category.superCategoryRow > 0 ? (
                                        <ViewerConsumer>
                                            {({ viewer }) => (
                                                <CategoryNotificationButton category={category} viewer={viewer} />
                                            )}
                                        </ViewerConsumer>
                                    ) : null
                                }
                                rightComponent={
                                    ['mesiac-s', 'mesic-s', 'soutez', 'sutaz'].indexOf(category?.slug) === -1 &&
                                    ['Dni s...', 'Dny s...'].indexOf(category?.superCategoryName) === -1 ? (
                                        <HideForVendor>
                                            <span className={styles.headingCreate}>
                                                <BigButtonView
                                                    icon={newArticle}
                                                    label={<FormattedMessage id='NEW_THREAD_BUTTON' />}
                                                    onClick={this.onNewThreadClick}
                                                />
                                            </span>
                                        </HideForVendor>
                                    ) : null
                                }
                            />
                        ) : (
                            <div className={styles.promoHeadingWrapper}>
                                <h1 className={styles.heading}>{category?.name}</h1>
                            </div>
                        )}
                    </div>
                    <Switch>
                        <RouteWithSearch
                            path='/forum/category/:slug'
                            exact={true}
                            render={props => (
                                <CategoryThreadsLoader
                                    {...props}
                                    category={category}
                                    isTargetedPromoCampaign={isTargetedPromoCampaign}
                                />
                            )}
                        />
                        <RouteWithSearch
                            path='/forum/private-discussions'
                            exact={true}
                            render={props => (
                                <CategoryThreadsLoader
                                    {...props}
                                    category={category}
                                    match={{
                                        params: { slug: site.isSlovak ? 'sukromne-diskusie' : 'soukrome-diskuze' },
                                    }}
                                />
                            )}
                        />
                    </Switch>
                    <div className={styles.title}>
                        <TitleView
                            title={
                                <h2 className={styles.searchHeading}>
                                    <FormattedMessage id='DIDNT_FIND_WHAT_YOU_LOOKING_FOR' />
                                </h2>
                            }
                        />
                        <SearchPanel
                            style={{ searchPanel: styles.searchPanel }}
                            target='forum'
                            submitOnToggle={false}
                        />
                        <HideForVendor>
                            <div className={styles.create}>
                                <span className={styles.createButton}>
                                    <BigButtonView
                                        icon={newArticle}
                                        label={<FormattedMessage id='NEW_THREAD_BUTTON' />}
                                        onClick={this.onNewThreadClick}
                                    />
                                </span>
                            </div>
                        </HideForVendor>
                    </div>
                </div>
                <NewThreadView innerRef={this.setNewThreadView} newThreadViewModel={newThreadViewModel} />
            </div>
        );
    }
}

export default CategoryView;
